import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase'; // Firebase 초기화 파일
import { Box, Button, TextField, Typography, Container, FormControlLabel, Checkbox } from '@mui/material';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';


const AdminWorkspaceEdit = () => {
    const { workspaceId } = useParams(); // URL 파라미터로 workspaceId 가져오기
    const navigate = useNavigate();
    const [workspaceData, setWorkspaceData] = useState(null); // 워크스페이스 데이터 상태
    const [loading, setLoading] = useState(false); // 로딩 상태 관리
    const [leftLogoFile, setLeftLogoFile] = useState(null); // 좌측 상단 로고 이미지 파일
    const [rightLogoFile, setRightLogoFile] = useState(null); // 우측 상단 로고 이미지 파일
    const [backgroundFile, setBackgroundFile] = useState(null); // 배경 이미지 파일
    const [guestbookBackgroundFile, setGuestbookBackgroundFile] = useState(null); // 방명록 작성 배경 이미지 파일

    // 프리셋 색상 팔레트
    const presetColors = [
        '#FFFFFF', '#F0F0F0', '#FFD700', '#FFA500',
        '#87CEEB', '#98FB98', '#FFB6C1', '#FF69B4',
        '#000000', '#2C3E50', '#8B4513', '#4B0082',
        '#FF1493', '#00CED1', '#FF4500', '#32CD32'
    ];

    useEffect(() => {
        // 워크스페이스 데이터 불러오기
        const fetchWorkspace = async () => {
            try {
                const workspaceRef = doc(db, 'workspaces', workspaceId);
                const workspaceSnap = await getDoc(workspaceRef);
                if (workspaceSnap.exists()) {
                    setWorkspaceData(workspaceSnap.data());
                } else {
                    alert('워크스페이스를 찾을 수 없습니다.');
                    navigate('/admin/workspaces');
                }
            } catch (error) {
                console.error('워크스페이스 불러오기 오류:', error);
            }
        };
        fetchWorkspace();
    }, [workspaceId, navigate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setWorkspaceData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleFileChange = (e, type) => {
        if (type === 'leftLogo') {
            setLeftLogoFile(e.target.files[0]);
        } else if (type === 'rightLogo') {
            setRightLogoFile(e.target.files[0]);
        } else if (type === 'background') {
            setBackgroundFile(e.target.files[0]);
        }
    };

    const uploadImage = async (file, folder) => {
        const storage = getStorage();
        const storageRef = ref(storage, `${folder}/${workspaceId}_${file.name}`);
        await uploadBytes(storageRef, file);
        return getDownloadURL(storageRef);
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const workspaceRef = doc(db, 'workspaces', workspaceId);

            // 이미지 업로드 및 URL 가져오기
            let leftLogoURL = workspaceData.leftLogo || null;
            let rightLogoURL = workspaceData.rightLogo || null;
            let backgroundURL = workspaceData.backgroundImage || null;
            let guestbookBackgroundURL = workspaceData.guestbookBackgroundImage || null;

            if (leftLogoFile) {
                leftLogoURL = await uploadImage(leftLogoFile, 'workspaces/logos');
            }
            if (rightLogoFile) {
                rightLogoURL = await uploadImage(rightLogoFile, 'workspaces/logos');
            }
            if (backgroundFile) {
                backgroundURL = await uploadImage(backgroundFile, 'workspaces/backgrounds');
            }
            if (guestbookBackgroundFile) {
                guestbookBackgroundURL = await uploadImage(guestbookBackgroundFile, 'workspaces/guestbookBackgrounds');
            }

            // Firestore 업데이트
            await updateDoc(workspaceRef, {
                ...workspaceData,
                leftLogo: leftLogoURL,
                rightLogo: rightLogoURL,
                backgroundImage: backgroundURL,
                guestbookBackgroundImage: guestbookBackgroundURL,
                nameColor: workspaceData.nameColor || '#FFFFFF',
                privacyAgreement: workspaceData.privacyAgreement || '',
                noticeMessage: workspaceData.noticeMessage || '',
                qrMessage: workspaceData.qrMessage || '',
            });

            alert('워크스페이스가 성공적으로 수정되었습니다.');
            navigate('/admin/workspaces'); // 수정 후 리스트 화면으로 이동
        } catch (error) {
            console.error('워크스페이스 수정 오류:', error);
            alert('수정 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    if (!workspaceData) {
        return (
            <Container maxWidth="lg" sx={{ textAlign: 'center', marginTop: '50px' }}>
                <Typography variant="h5">워크스페이스 데이터를 불러오는 중...</Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm" sx={{ marginTop: '50px' }}>
            <Typography variant="h4" gutterBottom>
                워크스페이스 수정
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                    label="워크스페이스 이름"
                    name="name"
                    value={workspaceData.name || ''}
                    onChange={handleInputChange}
                    fullWidth
                />
                <TextField
                    label="설명"
                    name="description"
                    value={workspaceData.description || ''}
                    onChange={handleInputChange}
                    fullWidth
                />

                {/* 메시지 승인 필요 여부 */}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={workspaceData.isApprovalRequired || false}
                            onChange={(e) => handleInputChange({
                                target: {
                                    name: 'isApprovalRequired',
                                    value: e.target.checked
                                }
                            })}
                            color="primary"
                        />
                    }
                    label="메시지 승인 필요"
                />

                {/* 로고 업로드 - 좌측 상단, 우측 상단 */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* 좌측 상단 로고 */}
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>좌측 상단 로고 등록</Typography>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileChange(e, 'leftLogo')}
                            style={{ marginBottom: 8 }}
                        />
                        {(workspaceData.leftLogo || leftLogoFile) && (
                            <img
                                src={leftLogoFile ? URL.createObjectURL(leftLogoFile) : workspaceData.leftLogo}
                                alt="좌측 상단 로고 미리보기"
                                style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', marginTop: 8 }}
                            />
                        )}
                    </Box>

                    {/* 우측 상단 로고 */}
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>우측 상단 로고 등록</Typography>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileChange(e, 'rightLogo')}
                            style={{ marginBottom: 8 }}
                        />
                        {(workspaceData.rightLogo || rightLogoFile) && (
                            <img
                                src={rightLogoFile ? URL.createObjectURL(rightLogoFile) : workspaceData.rightLogo}
                                alt="우측 상단 로고 미리보기"
                                style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', marginTop: 8 }}
                            />
                        )}
                    </Box>
                </Box>

                {/* 배경 이미지 업로드 */}
                <Typography>배경 이미지 업로드</Typography>
                <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'background')}
                />
                {workspaceData.backgroundImage && (
                    <img
                        src={workspaceData.backgroundImage}
                        alt="배경 미리보기"
                        style={{ width: '100%', objectFit: 'cover' }}
                    />
                )}

                {/* 방명록 작성 화면 배경 이미지 업로드 */}
                <Typography>방명록 작성 화면 배경 이미지 업로드</Typography>
                <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setGuestbookBackgroundFile(e.target.files[0])}
                />
                {workspaceData.guestbookBackgroundImage && !guestbookBackgroundFile && (
                    <img
                        src={workspaceData.guestbookBackgroundImage}
                        alt="방명록 작성 배경 미리보기"
                        style={{ width: '100%', objectFit: 'cover', marginBottom: 8 }}
                    />
                )}
                {guestbookBackgroundFile && (
                    <Typography variant="body2" color="textSecondary">
                        선택된 이미지: {guestbookBackgroundFile.name}
                    </Typography>
                )}

                {/* 개인정보 수집·이용 동의 내용 입력 */}
                <TextField
                    label="메시지 작성 시 유의사항"
                    multiline
                    minRows={2}
                    fullWidth
                    name="noticeMessage"
                    value={workspaceData.noticeMessage || ''}
                    onChange={handleInputChange}
                    placeholder="방명록 작성 화면에 표시될 유의사항 문구를 입력하세요."
                />
                <TextField
                    label="개인정보 수집·이용 동의 내용"
                    multiline
                    minRows={4}
                    fullWidth
                    name="privacyAgreement"
                    value={workspaceData.privacyAgreement || ''}
                    onChange={handleInputChange}
                    placeholder="방명록 작성 화면에 표시될 개인정보 동의 내용을 입력하세요."
                />

                {/* QR 코드 메시지 입력 */}
                <TextField
                    label="QR 코드 메시지"
                    fullWidth
                    name="qrMessage"
                    value={workspaceData.qrMessage || ''}
                    onChange={handleInputChange}
                    placeholder="QR 코드에 표시될 메시지를 입력하세요."
                />

                {/* 워크스페이스 이름 색상 선택 */}
                <Box>
                    <Typography variant="subtitle1" gutterBottom>
                        워크스페이스 이름 색상
                    </Typography>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(8, 50px)',
                        gridTemplateRows: 'repeat(2, 50px)',
                        gap: 1,
                        justifyContent: 'center',
                        mb: 2
                    }}>
                        {presetColors.map(color => (
                            <Box
                                key={color}
                                onClick={() => setWorkspaceData(prev => ({ ...prev, nameColor: color }))}
                                sx={{
                                    width: 50,
                                    height: 50,
                                    backgroundColor: color,
                                    border: (workspaceData.nameColor || '#FFFFFF') === color ? '4px solid #1976d2' : '2px solid #ccc',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        transform: 'scale(1.1)',
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                                    }
                                }}
                            />
                        ))}
                    </Box>

                    {/* 커스텀 색상 입력 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center', mt: 2 }}>
                        <Typography variant="body2">커스텀 색상:</Typography>
                        <input
                            type="color"
                            value={workspaceData.nameColor || '#FFFFFF'}
                            onChange={(e) => setWorkspaceData(prev => ({ ...prev, nameColor: e.target.value }))}
                            style={{
                                width: '60px',
                                height: '40px',
                                border: '2px solid #ccc',
                                borderRadius: '8px',
                                cursor: 'pointer'
                            }}
                        />
                        <TextField
                            size="small"
                            value={workspaceData.nameColor || '#FFFFFF'}
                            onChange={(e) => {
                                const value = e.target.value;
                                // Hex 색상 코드 검증 (# 포함 7자리 또는 4자리)
                                if (/^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/.test(value) || value === '#') {
                                    setWorkspaceData(prev => ({ ...prev, nameColor: value }));
                                }
                            }}
                            placeholder="#FFFFFF"
                            sx={{ width: '120px' }}
                            inputProps={{
                                style: { textTransform: 'uppercase' }
                            }}
                        />
                    </Box>

                    <Box
                        sx={{
                            p: 3,
                            backgroundColor: '#333',
                            borderRadius: '8px',
                            textAlign: 'center'
                        }}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                color: workspaceData.nameColor || '#FFFFFF',
                                fontWeight: 700,
                                fontFamily: 'GmarketSansMedium'
                            }}
                        >
                            미리보기: {workspaceData.name || '워크스페이스 이름'}
                        </Typography>
                    </Box>
                </Box>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={loading}
                >
                    {loading ? '저장 중...' : '저장'}
                </Button>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate('/admin/workspaces')}
                >
                    취소
                </Button>
            </Box>
        </Container>
    );
};

export default AdminWorkspaceEdit;
